import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const api = axios.create({ baseURL: API_BASE });

// Attach admin token to every request when present
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// ── Auth ────────────────────────────────────────────────
export const adminLogin = (username, password) =>
  api.post('/api/auth/login', { username, password });

// User login (same endpoint, different token storage key)
export const userLogin = (username, password) =>
  api.post('/api/auth/login', { username, password });

// ── Admin: Users ────────────────────────────────────────
export const fetchUsers = () =>
  api.get('/api/admin/users');

export const createUser = (username, password, role) =>
  api.post('/api/admin/users', { username, password, role });

export const updateUser = (userId, { username, password, role, active }) =>
  api.put(`/api/admin/users/${userId}`, { username, password, role, active });

export const deleteUser = (userId) =>
  api.delete(`/api/admin/users/${userId}`);

// ── Admin: Rooms ────────────────────────────────────────
export const fetchRooms = () =>
  api.get('/api/admin/rooms');

export const createRoom = (name, password, ownerId) =>
  api.post('/api/admin/rooms', { name, password, ownerId });

export const updateRoom = (roomId, { name, password, ownerId }) =>
  api.put(`/api/admin/rooms/${roomId}`, { name, password, ownerId });

export const clearRoomMessages = (roomId, filter = 'all') =>
  api.delete(`/api/admin/rooms/${roomId}/messages`, { params: { filter } });

export const deleteRoom = (roomId) =>
  api.delete(`/api/admin/rooms/${roomId}`);

// ── Admin: Room Members ─────────────────────────────────
export const fetchRoomMembers = (roomId) =>
  api.get(`/api/admin/rooms/${roomId}/members`);

export const addRoomMember = (roomId, userId) =>
  api.post(`/api/admin/rooms/${roomId}/members`, { userId });

export const removeRoomMember = (roomId, userId) =>
  api.delete(`/api/admin/rooms/${roomId}/members/${userId}`);

// ── Public: Chat ─────────────────────────────────────────
export const getRoomInfo = (linkToken) =>
  api.get(`/api/rooms/${linkToken}/info`);

export const verifyRoomPassword = (linkToken, password, senderName) =>
  api.post(`/api/rooms/${linkToken}/verify`, { password, senderName });

export const fetchMessages = (linkToken, roomToken) =>
  api.get(`/api/rooms/${linkToken}/messages`, {
    headers: { Authorization: `Bearer ${roomToken}` }
  });

// ── Room Owner: Clear messages ──────────────────────────
export const clearMessagesByOwner = (linkToken, token, filter = 'all') =>
  api.delete(`/api/rooms/${linkToken}/messages`, {
    params: { filter },
    headers: { Authorization: `Bearer ${token}` }
  });

// ── User: My Rooms ──────────────────────────────────────
// Fetches rooms owned by the logged-in user (for auto-join).
export const fetchMyRooms = (token) =>
  api.get('/api/rooms/my-rooms', {
    headers: { Authorization: `Bearer ${token}` }
  });

// Send a message via REST (for browser chat without WebSocket)
export const sendMessage = (linkToken, roomToken, content, senderName) =>
  api.post(`/api/rooms/${linkToken}/messages`,
    { content, senderName },
    { headers: { Authorization: `Bearer ${roomToken}` } }
  );

// ── Admin: Location Tracking ────────────────────────────
export const getTrackingStatus = (userId) =>
  api.get(`/api/admin/users/${userId}/tracking`);

export const setTracking = (userId, enabled) =>
  api.put(`/api/admin/users/${userId}/tracking`, { enabled });

export const getUserLocation = (userId) =>
  api.get(`/api/admin/users/${userId}/location`);

export const getUserLocationHistory = (userId) =>
  api.get(`/api/admin/users/${userId}/location/history`);

export const refreshUserLocation = (userId) =>
  api.post(`/api/admin/users/${userId}/location/refresh`);
